import React, { useEffect, useState } from 'react';
import { DEFAULT_GAME_PLAN, loadGamePlan, saveGamePlan } from '../../services/gamePlan';
import { STEP_TYPES } from '../../services/jobs';
import '../Routes/StatusUpdateSheet.css';
import './Settings.css';

/**
 * Settings → Game plan (admin edits, everyone can read): the wording every customer's game plan uses —
 * intro, what each step is + its usual timing, install length by package, the before-we-arrive checklist,
 * on the day, after. The customer's own steps and booked dates come from their Job.
 */
const GamePlanSettings = ({ isAdmin, onBack }) => {
  const [plan, setPlan] = useState(null);
  const [meta, setMeta] = useState({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState('');

  useEffect(() => {
    loadGamePlan()
      .then((out) => { setPlan(out.plan); setMeta(out); })
      .catch((err) => setError(err.message));
  }, []);
  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(''), 2600);
    return () => clearTimeout(t);
  }, [toast]);

  if (!plan) return <div className="set-page"><p className="set-muted">{error || 'Loading…'}</p></div>;

  const set = (patch) => setPlan({ ...plan, ...patch });
  const setStep = (key, field, value) => set({ steps: { ...plan.steps, [key]: { ...plan.steps[key], [field]: value } } });
  const setRule = (i, field, value) => set({ installLength: { ...plan.installLength, byPackage: plan.installLength.byPackage.map((r, k) => (k === i ? { ...r, [field]: value } : r)) } });
  const ro = !isAdmin;

  const save = async () => {
    setBusy(true);
    try {
      // the checklist is edited one item per line
      const clean = { ...plan, before: plan.before.map((s) => s.trim()).filter(Boolean) };
      setToast(await saveGamePlan(clean));
      setPlan(clean);
      setMeta({ updatedAt: new Date().toISOString(), updatedBy: 'you' });
    } catch (err) {
      setToast(`⚠ ${err.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="set-page">
      <div className="set-head">
        <button type="button" className="set-back" onClick={onBack} aria-label="Back">‹</button>
        <div>
          <h2>Game plan</h2>
          <p className="set-muted">
            The wording every customer's game plan uses. Their own steps, dates, installer and visits come from their Job.
            {meta.updatedAt ? ` Last saved ${new Date(meta.updatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}${meta.updatedBy ? ` by ${meta.updatedBy}` : ''}.` : ' Not saved yet — showing the draft.'}
          </p>
        </div>
      </div>
      {ro && <p className="set-note">Only an admin can change this.</p>}

      <section className="set-card">
        <h3>Opening line</h3>
        <textarea className="status-textarea" rows={3} value={plan.intro} readOnly={ro} onChange={(e) => set({ intro: e.target.value })} />
      </section>

      <section className="set-card">
        <h3>The steps</h3>
        <p className="set-muted">What each step is, and the usual timing shown until a real date is booked.</p>
        {STEP_TYPES.map((s) => (
          <div key={s.type} className="set-step">
            <b>{s.label}</b>
            <label>
              What it is
              <textarea className="status-textarea" rows={2} value={(plan.steps[s.type] || {}).about || ''} readOnly={ro} onChange={(e) => setStep(s.type, 'about', e.target.value)} />
            </label>
            <label>
              Usual timing
              <input value={(plan.steps[s.type] || {}).timing || ''} readOnly={ro} onChange={(e) => setStep(s.type, 'timing', e.target.value)} />
            </label>
          </div>
        ))}
      </section>

      <section className="set-card">
        <h3>How long the install takes</h3>
        <label>
          Usually
          <input value={plan.installLength.default} readOnly={ro} onChange={(e) => set({ installLength: { ...plan.installLength, default: e.target.value } })} />
        </label>
        <p className="set-muted">For a package whose name contains…</p>
        {plan.installLength.byPackage.map((r, i) => (
          <div key={i} className="set-rule">
            <input value={r.match} readOnly={ro} placeholder="e.g. Clean-Out" onChange={(e) => setRule(i, 'match', e.target.value)} />
            <input value={r.text} readOnly={ro} placeholder="e.g. Usually a full day." onChange={(e) => setRule(i, 'text', e.target.value)} />
            {!ro && (
              <button type="button" aria-label="Remove" onClick={() => set({ installLength: { ...plan.installLength, byPackage: plan.installLength.byPackage.filter((_, k) => k !== i) } })}>✕</button>
            )}
          </div>
        ))}
        {!ro && (
          <button type="button" className="set-link" onClick={() => set({ installLength: { ...plan.installLength, byPackage: [...plan.installLength.byPackage, { match: '', text: '' }] } })}>
            + Add a package rule
          </button>
        )}
      </section>

      <section className="set-card">
        <h3>Before we arrive</h3>
        <p className="set-muted">One item per line — each becomes a checklist point.</p>
        <textarea className="status-textarea" rows={6} value={plan.before.join('\n')} readOnly={ro} onChange={(e) => set({ before: e.target.value.split('\n') })} />
      </section>

      <section className="set-card">
        <h3>On the day</h3>
        <textarea className="status-textarea" rows={5} value={plan.onTheDay} readOnly={ro} onChange={(e) => set({ onTheDay: e.target.value })} />
      </section>

      <section className="set-card">
        <h3>After the install</h3>
        <textarea className="status-textarea" rows={3} value={plan.after} readOnly={ro} onChange={(e) => set({ after: e.target.value })} />
      </section>

      {!ro && (
        <div className="set-actions">
          <button type="button" className="status-cancel" disabled={busy} onClick={() => setPlan({ ...DEFAULT_GAME_PLAN })}>Reset to draft</button>
          <button type="button" className="status-save" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save game plan'}</button>
        </div>
      )}
      {toast && <div className="set-toast" role="status">{toast}</div>}
    </div>
  );
};

export default GamePlanSettings;
